import { useRef, useState, type KeyboardEvent } from 'react';
import { Delete, Phone, Smartphone } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { isMobileDevice, useTelephonyStore } from '../../store/telephonyStore';
import { isDialable, sanitizeDialInput } from '../../lib/telephony/dialer';

const KEYS: { key: string; sub?: string }[] = [
  { key: '1' }, { key: '2', sub: 'ABC' }, { key: '3', sub: 'DEF' },
  { key: '4', sub: 'GHI' }, { key: '5', sub: 'JKL' }, { key: '6', sub: 'MNO' },
  { key: '7', sub: 'PQRS' }, { key: '8', sub: 'TUV' }, { key: '9', sub: 'WXYZ' },
  { key: '*' }, { key: '0', sub: '+' }, { key: '#' },
];

const LONG_PRESS_MS = 500;

/**
 * Free-number dial pad, with the same two ways to call as CallButton: the
 * softphone (desk) or "ring my mobile, then dial" (away from the PC — the only
 * option on a phone, which can't hold the Twilio transport).
 */
export function DialPad({ onCallStarted }: { onCallStarted?: () => void }) {
  const [value, setValue] = useState('');
  const [bridgeMsg, setBridgeMsg] = useState<string | null>(null);
  const startCall = useTelephonyStore((s) => s.startCall);
  const bridgeCall = useTelephonyStore((s) => s.bridgeCall);
  const phase = useTelephonyStore((s) => s.phase);
  const inputRef = useRef<HTMLInputElement>(null);
  const longPress = useRef<{ timer: number; fired: boolean } | null>(null);

  const onPhone = isMobileDevice();
  const busy = phase !== 'idle' && phase !== 'ended';
  const dialable = isDialable(value);

  const append = (chars: string) => {
    setValue((v) => sanitizeDialInput(v + chars));
    inputRef.current?.focus();
  };

  // Long press on 0 types "+" (as on a phone keypad); a short press types 0.
  const pressStart = (key: string) => {
    if (key !== '0') return;
    const state = { fired: false, timer: 0 };
    state.timer = window.setTimeout(() => {
      state.fired = true;
      append('+');
    }, LONG_PRESS_MS);
    longPress.current = state;
  };
  const pressEnd = (key: string) => {
    const lp = longPress.current;
    longPress.current = null;
    if (lp) window.clearTimeout(lp.timer);
    if (!lp?.fired) append(key);
  };

  const callSoftphone = () => {
    if (!dialable || busy) return;
    void startCall(value);
    onCallStarted?.();
  };

  const callViaMobile = async () => {
    if (!dialable) return;
    setBridgeMsg('Appel…');
    const ok = await bridgeCall(value);
    setBridgeMsg(ok ? 'Ton mobile va sonner 📲' : 'Échec');
    window.setTimeout(() => setBridgeMsg(null), 4000);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (onPhone) void callViaMobile();
    else callSoftphone();
  };

  return (
    <div className="p-3">
      <div className="flex items-center gap-1 border-b border-desk-line pb-2">
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(sanitizeDialInput(e.target.value))}
          onKeyDown={onKeyDown}
          inputMode="tel"
          autoFocus={!onPhone}
          placeholder="Numéro"
          aria-label="Numéro à appeler"
          className="min-w-0 flex-1 bg-transparent text-center text-xl font-medium tracking-wide text-desk-ink outline-none placeholder:text-desk-faint"
        />
        {value && (
          <button
            type="button"
            onClick={() => setValue((v) => v.slice(0, -1))}
            className="rounded-md p-1.5 text-desk-muted hover:bg-desk-subtle hover:text-desk-soft"
            aria-label="Effacer le dernier chiffre"
          >
            <Delete className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="mt-2 grid grid-cols-3 gap-1.5">
        {KEYS.map(({ key, sub }) => (
          <button
            key={key}
            type="button"
            onPointerDown={() => pressStart(key)}
            onPointerUp={() => pressEnd(key)}
            onPointerLeave={() => {
              if (longPress.current) window.clearTimeout(longPress.current.timer);
              longPress.current = null;
            }}
            className="flex h-12 flex-col items-center justify-center rounded-lg bg-desk-subtle text-desk-ink transition-colors hover:bg-desk-hover active:bg-desk-selected"
          >
            <span className="text-lg font-medium leading-none">{key}</span>
            {sub && <span className="mt-0.5 text-[9px] font-medium tracking-widest text-desk-muted">{sub}</span>}
          </button>
        ))}
      </div>

      <div className="mt-3 flex gap-2">
        {!onPhone && (
          <button
            type="button"
            onClick={callSoftphone}
            disabled={!dialable || busy}
            title="Appeler depuis l'ordinateur (softphone)"
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-green-600 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Phone className="h-4 w-4" />
            Appeler
          </button>
        )}
        <button
          type="button"
          onClick={() => void callViaMobile()}
          disabled={!dialable}
          title="M'appeler sur mon mobile, puis composer le numéro"
          className={twMerge(
            'flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40',
            onPhone
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'border border-desk-line text-desk-soft hover:bg-desk-subtle',
          )}
        >
          <Smartphone className="h-4 w-4" />
          Sur mon mobile
        </button>
      </div>
      {bridgeMsg && <p className="mt-2 text-center text-xs text-desk-muted">{bridgeMsg}</p>}
    </div>
  );
}
